import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  StatCard,
  StatGrid,
  StatusPill,
  Stack,
  Text,
  Typography,
} from '@fse/ui';
import { useTranslation } from 'react-i18next';

import { readAccessTokenClaims } from '../api/client';
import { farmersControllerList } from '../api/generated/farmers/farmers';
import { healthControllerLiveness } from '../api/generated/health/health';
import { useEditRequestsControllerList } from '../api/generated/farmer-profile-edit-requests/farmer-profile-edit-requests';

// Roles allowed to see the Pending-reviews metric, mirroring the nav
// role-gating in `_app.tsx` (the PATCH endpoint behind the page 403s
// farmers, so the count is meaningless to them).
const REVIEW_ROLES = ['admin', 'coop_admin'];

const numberFormatter = new Intl.NumberFormat();

export function DashboardScreen() {
  const { t } = useTranslation();
  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: () => healthControllerLiveness(),
    refetchInterval: 30_000,
  });

  // Total-farmers metric. We only need the count, so request a single row
  // and read `pagination.total` (the list itself is rendered on /farmers).
  const farmersQuery = useQuery({
    queryKey: ['farmers', { limit: 1, offset: 0 }],
    queryFn: () => farmersControllerList({ limit: 1, offset: 0 }),
  });

  // Pending-reviews metric — admin / coop_admin only. The query is gated by
  // `enabled` so farmers never fire the request (it would 403 / be noise).
  const role = readAccessTokenClaims()?.role ?? null;
  const canSeeReviews = role !== null && REVIEW_ROLES.includes(role);
  const pendingReviewsQuery = useEditRequestsControllerList(
    { status: 'pending', limit: 1, offset: 0 },
    { query: { enabled: canSeeReviews } },
  );

  return (
    <Stack spacing={3}>
      <Text variant="headline-medium" as="h1">
        {t('dashboard.title')}
      </Text>

      <StatGrid columns={3}>
        <MetricCard
          label={t('dashboard.metric.totalFarmers')}
          color="leaf"
          icon="agriculture"
          isLoading={farmersQuery.isPending}
          isError={farmersQuery.isError}
          value={farmersQuery.data?.pagination.total}
        />
        {canSeeReviews ? (
          <MetricCard
            label={t('dashboard.metric.pendingReviews')}
            color="mango"
            icon="rate_review"
            isLoading={pendingReviewsQuery.isPending}
            isError={pendingReviewsQuery.isError}
            value={pendingReviewsQuery.data?.pagination.total}
          />
        ) : null}
        {/* Batches metric intentionally omitted: no batches hook is wired in
            apps/web/src/api yet (another agent owns that slice). Add a card
            here once a generated/manual batches list hook exists. */}
      </StatGrid>

      <Card>
        <CardHeader title={t('dashboard.health.title')} />
        <CardContent>
          {healthQuery.isPending ? (
            <Typography color="text.secondary">{t('dashboard.health.checking')}</Typography>
          ) : healthQuery.isError ? (
            <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
              <StatusPill variant="overdue" icon="error">
                {t('dashboard.health.unreachable')}
              </StatusPill>
              <Typography color="error">
                {t('dashboard.health.probeFailed', { error: String(healthQuery.error) })}
              </Typography>
            </Stack>
          ) : healthQuery.isSuccess ? (
            <StatusPill variant="active" icon="check_circle">
              {t('dashboard.health.reachable')}
            </StatusPill>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={t('dashboard.phase0.title')} />
        <CardContent>
          <Typography variant="body1" gutterBottom>
            {t('dashboard.phase0.intro')}
          </Typography>
          <ul style={{ marginTop: 0, paddingLeft: 20, lineHeight: 1.7 }}>
            <li>✓ {t('dashboard.phase0.itemUi')} <code>@fse/ui</code></li>
            <li>
              ✓ {t('dashboard.phase0.itemOrval')}{' '}
              <code>api.foodsecurityexchange.cloud</code>
            </li>
            <li>✓ {t('dashboard.phase0.itemRouter')}</li>
            <li>✓ {t('dashboard.phase0.itemQuery')}</li>
            <li>○ {t('dashboard.phase0.itemAuth')}</li>
            <li>○ {t('dashboard.phase0.itemPhase1')}</li>
          </ul>
        </CardContent>
      </Card>
    </Stack>
  );
}

interface MetricCardProps {
  label: string;
  value: number | undefined;
  isLoading: boolean;
  isError: boolean;
  color?: 'leaf' | 'mango' | 'blue' | 'purple' | 'neutral';
  icon?: string;
}

/**
 * At-a-glance summary tile, rendered as a Mango — Farmer Market `StatCard`.
 * Loading shows a neutral spinner in place of the value (design: no skeleton
 * shimmer); an errored query shows an em-dash rather than crashing the
 * dashboard (each metric fails independently of the others).
 */
function MetricCard({ label, value, isLoading, isError, color = 'neutral', icon }: MetricCardProps) {
  const display = isLoading ? (
    // height 1.1em matches the StatCard value line box (headlineLarge,
    // lineHeight 1.1) so the tile doesn't shift when the number lands;
    // `inherit` takes the tile's mode-aware foreground tone.
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '1.1em' }}
    >
      <CircularProgress size={24} color="inherit" />
    </Box>
  ) : isError || value == null ? (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      —
    </Box>
  ) : (
    numberFormatter.format(value)
  );

  return <StatCard label={label} value={display} color={color} icon={icon} />;
}
